import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { selfRegisterOrganization } from "../../setup/api/services/organization";
import TextInput from "../common/hookForm/TextInput";
import TextArea from "../common/hookForm/TextArea";
import PrimaryButton from "../common/PrimaryButton";

/** What the gate needs to render its shell for a just-submitted org, before any refetch. */
export interface ISubmittedOrganization {
  /** Required to attach onboarding documents to the org. */
  code: string;
  name: string;
  logoUrl?: string;
}

interface Props {
  onSubmitted: (organization: ISubmittedOrganization) => void;
  /** Pre-fills the form for a resubmission after rejection; code becomes read-only. */
  initialValues?: Partial<IFormValues>;
}

interface IFormValues {
  name: string;
  code: string;
  websiteUrl: string;
  description: string;
  logoUrl: string;
}

const EMPTY_VALUES: IFormValues = {
  name: "",
  code: "",
  websiteUrl: "",
  description: "",
  logoUrl: "",
};

const WEBSITE_URL_PATTERN = /^https?:\/\/[^\s]+\.[^\s]+$/i;

// Known backend business-rule messages (see OrganizationServiceImpl.selfRegister /
// resubmitRejectedOrganization in authentication-service), mapped to a meaningful Arabic
// message instead of the generic "something went wrong" fallback.
const KNOWN_ERROR_MESSAGES: Record<string, string> = {
  "code already exists": "هذا الرمز مستخدم بالفعل، الرجاء اختيار رمز آخر",
  "You already belong to an organization": "أنت مسجل بالفعل في مؤسسة",
  "Organization code cannot be changed on resubmission":
    "لا يمكن تغيير رمز المؤسسة عند إعادة الإرسال",
};

const CreateOrganizationForm: React.FC<Props> = ({
  onSubmitted,
  initialValues,
}) => {
  const { t } = useTranslation();
  const isResubmit = !!initialValues;
  const [values, setValues] = useState<IFormValues>({
    name: initialValues?.name ?? EMPTY_VALUES.name,
    code: initialValues?.code ?? EMPTY_VALUES.code,
    websiteUrl: initialValues?.websiteUrl ?? EMPTY_VALUES.websiteUrl,
    description: initialValues?.description ?? EMPTY_VALUES.description,
    logoUrl: initialValues?.logoUrl ?? EMPTY_VALUES.logoUrl,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField =
    (field: keyof IFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const isWebsiteValid = useMemo(
    () => WEBSITE_URL_PATTERN.test(values.websiteUrl.trim()),
    [values.websiteUrl]
  );

  const canSubmit =
    values.name.trim() !== "" &&
    values.code.trim() !== "" &&
    values.websiteUrl.trim() !== "" &&
    isWebsiteValid;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      setError(
        !isWebsiteValid && values.websiteUrl.trim() !== ""
          ? t("lbl_org_field_website_invalid")
          : t("lbl_org_required_field")
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const submitted = {
        code: values.code.trim(),
        name: values.name.trim(),
        logoUrl: values.logoUrl.trim() || undefined,
      };
      await selfRegisterOrganization({
        websiteUrl: values.websiteUrl.trim(),
        description: values.description.trim() || undefined,
        ...submitted,
      });
      onSubmitted(submitted);
    } catch (err: any) {
      const backendMessage = err?.error?.message;
      setError(
        (backendMessage && KNOWN_ERROR_MESSAGES[backendMessage]) ||
          t("lbl_org_submit_error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <div>
        <h2 className={styles.title}>{t("lbl_org_onboarding_title")}</h2>
        <p className={styles.subtitle}>{t("lbl_org_onboarding_subtitle")}</p>
      </div>
      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.field}>
          <TextInput
            name="name"
            value={values.name}
            onChange={setField("name")}
            onBlur={() => {}}
            placeholder={`${t("lbl_org_field_name")} *`}
          />
        </div>
        <div className={styles.field}>
          <TextInput
            name="code"
            value={values.code}
            onChange={setField("code")}
            onBlur={() => {}}
            disabled={isResubmit}
            placeholder={`${t("lbl_org_field_code")} *`}
          />
        </div>
        <div className={styles.field}>
          <TextInput
            name="websiteUrl"
            type="url"
            dir="ltr"
            value={values.websiteUrl}
            onChange={setField("websiteUrl")}
            onBlur={() => {}}
            placeholder={`${t("lbl_org_field_website")} *`}
            hint="https://example.com"
          />
        </div>
        <div className={styles.field}>
          <TextInput
            name="logoUrl"
            dir="ltr"
            value={values.logoUrl}
            onChange={setField("logoUrl")}
            onBlur={() => {}}
            placeholder={t("lbl_org_field_logo") as string}
            hint="https://example.com/logo.png"
          />
        </div>
        <div className={styles.field}>
          <TextArea
            name="description"
            value={values.description}
            onChange={setField("description")}
            label={t("lbl_org_field_description") as string}
            showLabel
            showMaxLength={false}
          />
        </div>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.submitRow}>
          <PrimaryButton type="submit" disabled={submitting || !canSubmit}>
            {t("lbl_org_submit")}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};

export default CreateOrganizationForm;
